import { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { Button, Flex, Div, GrowlNotification, Gutter, Logo, Panel, OptionListOption, Title, BodyText } from '@gaiads/telia-react-component-library';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  COUNTRIES,
  CustomPaginator,
  GenericTable,
  SearchAndFilter,
  SearchAndFilterOnChange,
  SearchWithScopeProps,
} from '@cdaas/reusable-components-daas';
import {
  AdminAssetSearchResponse,
  OrganizationResponse,
  UserWithUserGroupAndOrganizationList,
  UserWithUserGroupAndOrganizationResponse,
} from '@cdaas/api-clients/dist/asset-admin-openapi';

import { routes, withCustomerOrgFormPath } from '../App/routes';
import { assetAdminClient, assetAdminOrganizationClient, assetAdminUserClient } from '../../api-client/asset-admin-client';
import { resetOrderState } from '../../store/order';
import { clearSessionStorageOrderState } from '../../utils';

import styles from './FrontPage.module.scss';
import { selectFeatureFlags } from '../../store/featureFlags';

const pageSize = 10;

enum CustomerSortableColumns {
  Tscid,
  Name,
  Country,
}

enum UserSearchSort {
  Name = 'name',
  NameMinus = '-name',
  Email = 'email',
  EmailMinus = '-email',
  Customer = 'organization',
  CustomerMinus = '-organization',
}

enum SearchScope {
  Customer = 'Customer',
  Device = 'Device',
  User = 'User',
}

export default function FrontPage() {
  const maintenanceModeEnabled = useSelector(selectFeatureFlags).FEATURE_FLAG_MAINTENANCE_MODE_ENABLED;
  const headerT = useTranslation('siteHeader').t;
  const { t } = useTranslation('frontPage');
  const history = useHistory();
  const dispatch = useDispatch();

  const { instance } = useMsal();

  const scopeOptions: OptionListOption[] = [
    {
      label: t('customerScope'),
      value: SearchScope.Customer,
    },
    {
      label: t('deviceScope'),
      value: SearchScope.Device,
    },
    {
      label: t('userScope'),
      value: SearchScope.User,
    },
  ];

  const [searchScope, setSearchScope] = useState<SearchScope>(SearchScope.Customer);
  const [searchText, setSearchText] = useState('');

  const [allCustomerOrganizations, setAllCustomerOrganizations] = useState<OrganizationResponse[]>([]);
  const [filteredCustomerOrganizations, setFilteredCustomerOrganizations] = useState<OrganizationResponse[]>([]);
  const [currentPageCustomerOrganizations, setCurrentPageCustomerOrganizations] = useState<OrganizationResponse[]>([]);

  const [customerSort, setCustomerSort] = useState<CustomerSortableColumns>();

  const [devices, setDevices] = useState<AdminAssetSearchResponse[]>();
  const [invalidDeviceImeiValidationError, setInvalidDeviceImeiValidationError] = useState(false);

  const [loading, setLoading] = useState(true);
  const [errorGettingCustomerOrganizations, setErrorGettingCustomerOrganizations] = useState(false);
  const [errorGettingDevices, setErrorGettingDevices] = useState(false);
  const [maintenanceModeErrorDismissed, setMaintenanceModeErrorDismissed] = useState(false);

  const [pagedUsers, setPagedUsers] = useState<UserWithUserGroupAndOrganizationList>();
  const [pagedUsersSort, setPagedUsersSort] = useState<UserSearchSort>(UserSearchSort.Name);
  const [errorFetchingUsers, setErrorFetchingUsers] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const customerOrgsResponse = await assetAdminOrganizationClient.adminOrganizationList(undefined, undefined, 9999);
        setAllCustomerOrganizations(customerOrgsResponse.data.contents);
        setFilteredCustomerOrganizations(customerOrgsResponse.data.contents);
      } catch {
        setErrorGettingCustomerOrganizations(true);
      } finally {
        setLoading(false);
      }
    })();
    // clear ordering related items from session storage to ensure state is reset when switching between customer organizations
    clearSessionStorageOrderState();
  }, []);

  const handleCustomersPageChange = (pageItems: OrganizationResponse[]) => {
    setCurrentPageCustomerOrganizations(pageItems);
  };

  const handleCustomerSearchChange = (searchTextInput: string) => {
    const filteredCustomerOrganizations = !searchTextInput
      ? allCustomerOrganizations
      : allCustomerOrganizations.filter(org => {
          return (
            org.tsc_id?.toLowerCase().includes(searchTextInput.toLowerCase()) ||
            org.name.toLowerCase().includes(searchTextInput.toLowerCase()) ||
            org.id?.toLowerCase().includes(searchTextInput.toLowerCase())
          );
        });
    const currentPageCustomerOrganizations = filteredCustomerOrganizations.slice(0, pageSize);
    setFilteredCustomerOrganizations(filteredCustomerOrganizations);
    handleCustomersPageChange(currentPageCustomerOrganizations);
  };

  const handleCustomersSortChange = (sortColumn: CustomerSortableColumns) => {
    const sortedCustomerOrgs = [...filteredCustomerOrganizations];
    if (sortColumn === customerSort) {
      sortedCustomerOrgs.reverse();
    } else {
      if (sortColumn === CustomerSortableColumns.Name) {
        sortedCustomerOrgs.sort((a, b) => (a.name && b.name && a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1));
      } else if (sortColumn === CustomerSortableColumns.Tscid) {
        sortedCustomerOrgs.sort((a, b) => (a.tsc_id && b.tsc_id && a.tsc_id.toLowerCase() < b.tsc_id.toLowerCase() ? -1 : 1));
      } else if (sortColumn === CustomerSortableColumns.Country) {
        sortedCustomerOrgs.sort((a, b) => {
          const aCountry = COUNTRIES.find(ccc => ccc.value === a.locale.country)?.countryName;
          const bCountry = COUNTRIES.find(ccc => ccc.value === b.locale.country)?.countryName;
          return aCountry && bCountry && aCountry < bCountry ? -1 : 1;
        });
      }
    }
    setCustomerSort(sortColumn);
    setFilteredCustomerOrganizations(sortedCustomerOrgs);
    const currentPageCustomerOrganizations = sortedCustomerOrgs.slice(0, pageSize);
    handleCustomersPageChange(currentPageCustomerOrganizations);
  };

  const handleViewExistingCustomerClick = (customerOrganizationId: string) => {
    dispatch(resetOrderState());
    history.push(`/customer/${customerOrganizationId}`);
  };

  const handleViewNewCustomerClick = () => {
    history.push('/customer');
  };

  const getDevicesErrorText = () => {
    if (searchScope !== SearchScope.Device) {
      return undefined;
    } else if (invalidDeviceImeiValidationError && searchText.length > 0) {
      return t('errorInvalidDeviceImeiValidationText');
    } else if (errorGettingDevices) {
      return t('errorGettingDevicesText');
    } else if (devices?.length === 0) {
      return t('noDevicesFoundText');
    }
    return undefined;
  };

  const searchDevices = async (imeiInput: string) => {
    const imei = imeiInput.replaceAll(/\s+/g, '');
    // IMEI should be 15 or 16 digit long number
    if ((imei.length !== 15 && imei.length !== 16) || !/^\d+$/.test(imei)) {
      setInvalidDeviceImeiValidationError(true);
      return;
    }
    setLoading(true);
    try {
      const devicesResponse = await assetAdminClient.adminAssetSearch(imei);
      setDevices(devicesResponse.data);
    } catch {
      setErrorGettingDevices(true);
    } finally {
      setLoading(false);
    }
  };

  const fetchPagedUsers = async (search: string, sort: UserSearchSort, page: number) => {
    if (!search) return;
    setLoading(true);
    try {
      const res = (await assetAdminUserClient.adminListAllUsers(search, [sort], page, pageSize)).data;
      setPagedUsers(res);
    } catch {
      setErrorFetchingUsers(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (values: SearchAndFilterOnChange) => {
    const searchTextValue = values.search?.value ?? '';
    setSearchScope(values.search?.scope?.value as SearchScope);
    setSearchText(searchTextValue);
    if (values.search?.scope?.value === SearchScope.Customer) {
      handleCustomerSearchChange(searchTextValue);
    } else if (values.search?.scope?.value === SearchScope.Device) {
      setInvalidDeviceImeiValidationError(false);
      setErrorGettingDevices(false);
      searchDevices(searchTextValue);
    } else if (values.search?.scope?.value === SearchScope.User) {
      setErrorFetchingUsers(false);
      fetchPagedUsers(searchTextValue, pagedUsersSort, 0);
    }
  };

  const handleViewDeviceClick = (asset: AdminAssetSearchResponse) => {
    history.push(`${withCustomerOrgFormPath(routes.devices, asset.organization.id)}/${asset.id}`);
  };

  const getInactiveUserBodyText = (name: string | undefined) =>
    name ?? <BodyText style={{ fontStyle: 'italic' }}>{t('usersTable.inactiveUser')}</BodyText>;

  return (
    <Flex className={styles['front-page__container']} occupyHorizontalSpace occupyVerticalSpace={false}>
      <Flex directionColumn centeredHorizontally occupyHorizontalSpace className={styles['front-page__panel-container']}>
        <Flex alignRight>
          <Button iconName="logout" iconOnRight={true} variant="secondaryWhite" onClick={() => instance.logout()}>
            {t('logOutButton')}
          </Button>
        </Flex>
        <Gutter />
        <Panel className={styles['front-page__panel']} showOverflow>
          <Panel.Content className={styles['front-page__header']}>
            <Logo size="lg" color="purple500" />
            <Gutter />
            <Title size={'xs'} color="gray500">
              {headerT('customer_support_tool_name')}
            </Title>
          </Panel.Content>
          <Panel.Content className={styles['front-page__content']}>
            <Flex alignBaseline>
              <BodyText>{t('newCustomerQuestion')}</BodyText>
              <Gutter size="sm" />
              <BodyText clickableAppearance onClick={handleViewNewCustomerClick} color={'purple500'}>
                {t('createNewCustomer')}
              </BodyText>
            </Flex>
            <Div className={styles['front-page__search-input-container']}>
              <SearchAndFilter
                onChangeCallback={handleSearchChange}
                searchConfig={{
                  useDebounceInTextSearch: searchScope === SearchScope.Customer ? 0 : 1000,
                  searchInputOrSearchInputWithScopeProps: {
                    name: 'organization-or-device-or-user-search',
                    showLabel: true,
                    label: t('findCustomerOrDeviceOrUserLabel'),
                    errorText: getDevicesErrorText(), // For customers show possible error text in GenericTable
                    placeholder: t('searchPlaceholder'),
                    helperText:
                      searchScope === SearchScope.Device ? t('deviceHelperText') : searchScope === SearchScope.User ? t('userHelperText') : undefined,
                    scopeOptions: scopeOptions,
                  } as SearchWithScopeProps,
                }}
              />
            </Div>
            <Gutter size="lg" />

            {searchScope === SearchScope.Customer && (
              <>
                <GenericTable
                  loading={loading ? 'long' : 'none'}
                  errorText={errorGettingCustomerOrganizations ? t('errorGettingCustomerOrgsText') : undefined}
                  noDataText={t('noCustomerOrgsFoundText')}
                  inputData={currentPageCustomerOrganizations}
                  buttonConfig={{
                    name: t('customerOrgsTable.open'),
                    buttonClickCallback: (row: OrganizationResponse) => handleViewExistingCustomerClick(row.id || ''),
                    buttonProps: {
                      variant: 'secondary',
                      iconName: 'chevron-right',
                      size: 'md',
                      iconOnRight: true,
                    },
                  }}
                  rowConfigs={[
                    {
                      header: t('customerOrgsTable.customer'),
                      key: 'name',
                      fullyExternalSortFunction: () => handleCustomersSortChange(CustomerSortableColumns.Name),
                    },
                    {
                      header: t('customerOrgsTable.tscid'),
                      key: 'tsc_id',
                      fullyExternalSortFunction: () => handleCustomersSortChange(CustomerSortableColumns.Tscid),
                    },
                    {
                      header: t('customerOrgsTable.country'),
                      key: 'locale.country',
                      formatValue: (_value, row) => COUNTRIES.find(ccc => ccc.value === row.locale.country)?.countryName || '',
                      fullyExternalSortFunction: () => handleCustomersSortChange(CustomerSortableColumns.Country),
                      hideAtBreakpoint: 'xs',
                    },
                  ]}
                />
                <CustomPaginator
                  activateInputData={{
                    inputData: filteredCustomerOrganizations,
                    pageSize,
                  }}
                  onPageChange={pageChangeInfo => handleCustomersPageChange(pageChangeInfo.nextPageItems)}
                />
              </>
            )}

            {searchScope === SearchScope.Device && !!devices && devices.length > 0 && (
              <GenericTable
                loading={loading ? 'long' : 'none'}
                noDataText="" // This table should not be rendered at all if no devices were found
                inputData={devices}
                buttonConfig={{
                  name: t('devicesTable.open'),
                  buttonClickCallback: (row: AdminAssetSearchResponse) => handleViewDeviceClick(row),
                  buttonProps: {
                    variant: 'secondary',
                    iconName: 'chevron-right',
                    size: 'md',
                    iconOnRight: true,
                  },
                }}
                rowConfigs={[
                  { header: t('devicesTable.imei'), key: 'serial_number.number' },
                  { header: t('devicesTable.device'), key: 'product.name', hideAtBreakpoint: 'xs' },
                  { header: t('devicesTable.customer'), key: 'organization.name' },
                ]}
              />
            )}

            {searchScope === SearchScope.User && !!searchText && (
              <>
                <GenericTable
                  loading={loading ? 'long' : 'none'}
                  errorText={errorFetchingUsers ? t('errorFetchingUsers') : undefined}
                  noDataText={t('noUsersFound')}
                  inputData={pagedUsers?.contents}
                  buttonConfig={{
                    name: t('usersTable.open'),
                    buttonClickCallback: (row: UserWithUserGroupAndOrganizationResponse) =>
                      history.push(`/customer/${row.user_group.organization.id}/usergroups/${row.user_group.id}/users/${row.id}`),
                    buttonProps: {
                      variant: 'secondary',
                      iconName: 'chevron-right',
                      size: 'md',
                      iconOnRight: true,
                    },
                  }}
                  rowConfigs={[
                    {
                      header: t('usersTable.name'),
                      key: 'name',
                      formatValue: getInactiveUserBodyText,
                      fullyExternalSortFunction: () => {
                        setPagedUsersSort(prevState => {
                          const newSort = prevState === UserSearchSort.Name ? UserSearchSort.NameMinus : UserSearchSort.Name;
                          fetchPagedUsers(searchText, newSort, 0);
                          return newSort;
                        });
                      },
                    },
                    {
                      header: t('usersTable.email'),
                      key: 'email',
                      fullyExternalSortFunction: () => {
                        setPagedUsersSort(prevState => {
                          const newSort = prevState === UserSearchSort.Email ? UserSearchSort.EmailMinus : UserSearchSort.Email;
                          fetchPagedUsers(searchText, newSort, 0);
                          return newSort;
                        });
                      },
                    },
                    {
                      header: t('usersTable.customer'),
                      key: 'user_group.organization.name',
                      fullyExternalSortFunction: () => {
                        setPagedUsersSort(prevState => {
                          const newSort = prevState === UserSearchSort.Customer ? UserSearchSort.CustomerMinus : UserSearchSort.Customer;
                          fetchPagedUsers(searchText, newSort, 0);
                          return newSort;
                        });
                      },
                    },
                  ]}
                />
                {!!pagedUsers?.page_metadata.total_items && (
                  <CustomPaginator
                    activatePageInfoObject={pagedUsers?.page_metadata}
                    onPageChange={pageChangeInfo => fetchPagedUsers(searchText, pagedUsersSort, pageChangeInfo.nextChosenPageNumber)}
                  />
                )}
              </>
            )}
          </Panel.Content>
        </Panel>
      </Flex>
      {maintenanceModeEnabled && !maintenanceModeErrorDismissed && (
        <GrowlNotification
          notification={t('warningMaintenanceModeEnabled')}
          variant="warning"
          closeOnClick={() => setMaintenanceModeErrorDismissed(true)}
        />
      )}
    </Flex>
  );
}
